import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { GanttChart, ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await authAPI.forgotPassword(email);
      setSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-zinc-900 p-12 flex-col justify-between">
        <div>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <GanttChart className="w-6 h-6 text-white" />
            </div>
            <span className="font-outfit font-bold text-2xl text-white">ProjectOS</span>
          </Link>
        </div>
        <div className="max-w-md">
          <h2 className="font-outfit text-4xl font-bold text-white mb-4">
            Forgot Your Password?
          </h2>
          <p className="text-zinc-400 text-lg">
            No worries! Enter your email and we'll send you a link to reset your password.
          </p>
        </div>
        <div className="text-zinc-500 text-sm">
          Remember your password?{' '}
          <Link to="/login" className="text-white hover:underline">Sign in</Link>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col">
        <div className="p-6 flex justify-between items-center">
          <Link to="/login" className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>
          <div className="lg:hidden flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <GanttChart className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-bold text-xl">ProjectOS</span>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            {sent ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="font-outfit text-3xl font-bold text-zinc-900 mb-2">
                  Check Your Email
                </h1>
                <p className="text-zinc-600 mb-8">
                  We've sent a password reset link to<br />
                  <strong>{email}</strong>
                </p>
                <p className="text-sm text-zinc-500 mb-6">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button 
                    onClick={() => setSent(false)} 
                    className="text-primary hover:underline"
                  >
                    try again
                  </button>
                </p>
                <Link to="/login">
                  <Button variant="outline" className="w-full h-12 rounded-xl">
                    Back to Login
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8 text-orange-600" />
                </div>
                <h1 className="font-outfit text-3xl font-bold text-zinc-900 mb-2 text-center">
                  Reset Password
                </h1>
                <p className="text-zinc-600 mb-8 text-center">
                  Enter your email and we'll send you a reset link.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-zinc-700">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="forgot-email-input"
                      className="h-12 rounded-xl border-zinc-200 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                    className="w-full h-12 bg-primary hover:bg-orange-600 text-white rounded-xl font-medium transition-all active:scale-[0.98]"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Link'}
                  </Button>
                </form>

                <p className="mt-8 text-center text-zinc-600">
                  Remember your password?{' '}
                  <Link to="/login" className="text-primary hover:underline font-medium">
                    Sign in
                  </Link>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
